import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/api/client';
import { isDesktopApp } from '@/lib/desktop';

// ─────────────────────────────────────────────────────────────
// SaaS subscription (P0.3): state, plans, payments and the platform's
// public status. Confirming a payment and the licence register need the
// publisher token, which this client never holds.
//
// Contrats serveur :
//   GET  /api/billing/state                 (billing:read)
//   POST /api/billing/plan                  (billing:manage)
//   POST /api/billing/payments              (billing:manage)
//   GET  /api/status                        (public)
//
// Piège Prisma : les Decimal sérialisent en chaîne — le serveur les
// convertit déjà via Number() (billing.service.ts), mais les dates
// arrivent en chaînes ISO côté client (types string ici).
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Types — abonnement
// ─────────────────────────────────────────────────────────────

export type PlanCode = 'FACTURATION' | 'GESTION' | 'SUITE';

export type SubscriptionStatusCode =
  | 'TRIALING'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'SUSPENDED'
  | 'CANCELED';

export type PaymentMethodCode = 'MANUEL' | 'VIREMENT' | 'ORANGE_MONEY' | 'PISPI';

/**
 * Currency the platform states its plan prices in (`constants/plans.ts` of
 * the API, `PLAN_PRICE_*` in franc CFA); a subscription carries its own.
 */
export const PLAN_PRICE_CURRENCY = 'XOF';

export interface SerializedSubscription {
  id: string;
  /** Plan de la PROCHAINE période (downgrade différé possible). */
  plan: string;
  status: string;
  billingCycle: string;
  priceAmount: number;
  currency: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  graceUntil: string | null;
  canceledAt: string | null;
}

export interface SerializedPayment {
  id: string;
  amount: number;
  method: string;
  reference: string | null;
  status: string;
  periodStart: string;
  periodEnd: string;
  paidAt: string | null;
  createdAt: string;
}

export interface BillingState {
  /** Plan EFFECTIF du tenant. */
  plan: string;
  planLabel: string;
  priceMonthly: number;
  /** Fonctionnalités incluses dans le plan effectif (codes). */
  features: string[];
  status: string;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  subscription: SerializedSubscription | null;
  payments: SerializedPayment[];
}

export interface PlanChangeResult {
  plan: string;
  nextPlan: string;
  mode: 'IMMEDIAT' | 'A_L_ECHEANCE';
  effectiveAt: string | null;
  message: string;
}

export interface InitiatePaymentResult {
  payment: SerializedPayment;
  /** Instructions de paiement françaises retournées par le serveur. */
  instructions: string;
}

// ─────────────────────────────────────────────────────────────
// Types — statut public de la plateforme
// ─────────────────────────────────────────────────────────────

/**
 * The PUBLIC status contract, and nothing more. The route deliberately drops
 * the application version, the outbox counters, the worker timestamps and the
 * database latency — they would help an attacker map the instance — and a
 * test pins the exact key set (status-routes.test.ts). Declaring them here
 * made the page read undefined and throw on its own success branch.
 */
export interface PlatformStatus {
  status: 'ok' | 'degraded';
  sfeVersion: string;
  db: { ok: boolean };
  /** Secondes depuis le démarrage du processus API. */
  uptime: number;
}

// ─────────────────────────────────────────────────────────────
// Libellés français (affichage uniquement — AUCUNE valeur fiscale)
// ─────────────────────────────────────────────────────────────

export const PLAN_LABELS: Record<PlanCode, string> = {
  FACTURATION: 'Facturation',
  GESTION: 'Gestion',
  SUITE: 'Suite',
};

export function planLabel(code: string): string {
  return (PLAN_LABELS as Record<string, string>)[code] ?? code;
}

/** Ordre croissant des plans (upgrade/downgrade). */
export const PLAN_ORDER: readonly PlanCode[] = ['FACTURATION', 'GESTION', 'SUITE'];

/**
 * MIROIR D'AFFICHAGE des fonctionnalités par plan —
 * source de vérité serveur : packages/api/src/constants/plans.ts
 * (le gating réel est appliqué côté API ; ce miroir sert uniquement
 * à la grille de comparaison des offres).
 */
export const PLAN_FEATURES: Record<PlanCode, readonly string[]> = {
  FACTURATION: ['invoices', 'pos', 'stock', 'clients', 'articles', 'quotes', 'deliveries'],
  GESTION: [
    'invoices',
    'pos',
    'stock',
    'clients',
    'articles',
    'quotes',
    'deliveries',
    'accounting',
    'suppliers',
    'purchases',
    'reports',
    'declarations',
  ],
  SUITE: [
    'invoices',
    'pos',
    'stock',
    'clients',
    'articles',
    'quotes',
    'deliveries',
    'accounting',
    'suppliers',
    'purchases',
    'reports',
    'declarations',
    'hr',
    'payroll',
    'crm',
    'projects',
    'webhooks',
  ],
};

export const PLAN_FEATURE_LABELS: Record<string, string> = {
  invoices: 'Facturation électronique (FEC/MCF)',
  pos: 'Point de vente (POS)',
  stock: 'Gestion de stock',
  clients: 'Clients',
  articles: 'Articles',
  quotes: 'Devis / proforma',
  deliveries: 'Bons de livraison',
  accounting: 'Comptabilité SYSCOHADA',
  suppliers: 'Fournisseurs',
  purchases: 'Achats (bons de commande)',
  reports: 'Rapports & exports',
  declarations: 'Déclarations fiscales',
  hr: 'Ressources humaines',
  payroll: 'Paie',
  crm: 'CRM',
  projects: 'Gestion de projets',
  webhooks: 'Webhooks & intégrations',
};

export function planFeatureLabel(code: string): string {
  return PLAN_FEATURE_LABELS[code] ?? code;
}

/** Plan minimal (le moins cher) incluant la fonctionnalité — miroir UI. */
export function requiredPlanForFeature(feature: string): PlanCode {
  for (const code of PLAN_ORDER) {
    if (PLAN_FEATURES[code].includes(feature)) return code;
  }
  return 'SUITE';
}

export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  TRIALING: 'Essai en cours',
  ACTIVE: 'Actif',
  PAST_DUE: 'Paiement en retard',
  SUSPENDED: 'Suspendu (lecture seule)',
  CANCELED: 'Résilié',
};

export function subscriptionStatusLabel(status: string): string {
  return SUBSCRIPTION_STATUS_LABELS[status] ?? status;
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  MANUEL: 'Manuel (équipe commerciale)',
  VIREMENT: 'Virement bancaire',
  ORANGE_MONEY: 'Orange Money',
  PISPI: 'PI-SPI (paiement instantané)',
};

export const PAYMENT_METHOD_OPTIONS: { value: PaymentMethodCode; label: string }[] = [
  { value: 'VIREMENT', label: PAYMENT_METHOD_LABELS['VIREMENT']! },
  { value: 'ORANGE_MONEY', label: PAYMENT_METHOD_LABELS['ORANGE_MONEY']! },
  { value: 'PISPI', label: PAYMENT_METHOD_LABELS['PISPI']! },
  { value: 'MANUEL', label: PAYMENT_METHOD_LABELS['MANUEL']! },
];

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmé',
  FAILED: 'Échoué',
  CANCELED: 'Annulé',
};

export function paymentStatusLabel(status: string): string {
  return PAYMENT_STATUS_LABELS[status] ?? status;
}

/** Jours restants (entier ≥ 0) avant une échéance ISO — J-N de l'essai. */
export function daysUntil(dateIso: string): number {
  const diff = new Date(dateIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

// ─────────────────────────────────────────────────────────────
// Query keys
// ─────────────────────────────────────────────────────────────

export const billingKeys = {
  all: ['billing'] as const,
  state: () => [...billingKeys.all, 'state'] as const,
  status: () => [...billingKeys.all, 'platform-status'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API
// ─────────────────────────────────────────────────────────────

async function fetchBillingState(): Promise<BillingState> {
  const result = await apiClient.get<{ billing: BillingState }>('/billing/state');
  return result.billing;
}

async function changePlan(plan: string): Promise<PlanChangeResult> {
  const result = await apiClient.post<{ change: PlanChangeResult }>('/billing/plan', {
    plan,
  });
  return result.change;
}

async function initiatePayment(method: string): Promise<InitiatePaymentResult> {
  return apiClient.post<InitiatePaymentResult>('/billing/payments', { method });
}

/**
 * GET /api/status answers 503 when the DB is unreachable, so an
 * HTTP-code-only uptime probe sees a real outage. The minimal public body
 * still travels under `error.details`, so this page renders the degraded
 * state instead of the generic "unreachable" fallback.
 */
function isPlatformStatusShape(value: unknown): value is PlatformStatus {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'db' in value &&
    'uptime' in value
  );
}

async function fetchPlatformStatus(): Promise<PlatformStatus> {
  try {
    return await apiClient.get<PlatformStatus>('/status');
  } catch (err) {
    if (err instanceof ApiError && err.status === 503 && isPlatformStatusShape(err.details)) {
      return err.details;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────
// Hooks — abonnement
// ─────────────────────────────────────────────────────────────

/**
 * État d'abonnement du tenant (plan, statut, essai, paiements).
 * GET /api/billing/state — billing:read.
 */
export function useBillingState(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: billingKeys.state(),
    queryFn: fetchBillingState,
    staleTime: 1000 * 60, // 1 minute — le bandeau d'essai reste frais
    // The desktop app has no subscription (its API skips plan and
    // subscription checks): no state, so the trial banner stays hidden and
    // PlanGate / the sidebar stay open, as they do without billing:read.
    enabled: !isDesktopApp() && (options?.enabled ?? true),
  });
}

/**
 * Changement de plan : upgrade immédiat, downgrade à l'échéance
 * (le serveur décide — le message retourné l'explique).
 * POST /api/billing/plan — billing:manage.
 */
export function useChangePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (plan: string) => changePlan(plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

/**
 * Initie un paiement d'abonnement (SubscriptionPayment PENDING +
 * instructions françaises). POST /api/billing/payments — billing:manage.
 */
export function useInitiatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (method: string) => initiatePayment(method),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.state() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Hook — statut public de la plateforme
// ─────────────────────────────────────────────────────────────

/**
 * Statut public de la plateforme (page de statut, aucune donnée
 * sensible). GET /api/status — PUBLIC, rate-limité (30/min) :
 * rafraîchissement toutes les 60 s.
 */
export function usePlatformStatus() {
  return useQuery({
    queryKey: billingKeys.status(),
    queryFn: fetchPlatformStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

// ─────────────────────────────────────────────────────────────
// Exports utilitaires
// ─────────────────────────────────────────────────────────────

export {
  fetchBillingState,
  changePlan,
  initiatePayment,
  fetchPlatformStatus,
};
